import React, {
  useState,
  Dispatch,
  RefObject,
  forwardRef,
  SetStateAction,
  useImperativeHandle,
  ForwardRefRenderFunction,
} from 'react';
import { Button, Menu, Box, Grid, Tabs, Tab } from '@mui/material';

import FilterComponent from 'components/Filters/FilterComponent';

import { FilterData } from 'types/types';
import { LayersRef } from '../Layers/Layers';

const a11yProps = (index: number) => {
  return {
    id: `tab-${index}`,
    'aria-controls': `tabpanel-${index}`,
  };
};

interface TabPanelProps {
  index: number;
  value: number;
  children?: React.ReactNode;
}

const TabPanel: React.FC<TabPanelProps> = ({
  children,
  value,
  index,
  ...other
}) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 2, pt: 0 }}>{children}</Box>}
    </div>
  );
};

interface SettingsFiltersProps {
  dashboardId: string;
  closeSettings: () => void;
  initialFilters: FilterData[];
  anchorEl: HTMLElement | null;
  applySettingsClicked: () => void;
  globalLayersControlRef: RefObject<LayersRef>;
  setAnchorEl: Dispatch<SetStateAction<HTMLElement | null>>;
}

export interface SettingsFiltersRef {
  activeTab: number;
  openSettingsMenu: boolean;
  handleActiveTab: (activeTab: number) => void;
  handleOpenSettingsMenu: (openSettingMenu: boolean) => void;
  toggleSettings: (e: React.MouseEvent<HTMLElement>) => void;
}

const SettingsFilters: ForwardRefRenderFunction<
  SettingsFiltersRef,
  SettingsFiltersProps
> = (
  {
    anchorEl,
    setAnchorEl,
    dashboardId,
    closeSettings,
    initialFilters,
    globalLayersControlRef,
    applySettingsClicked,
  },
  ref
) => {
  const [activeTab, setActiveTab] = useState<number>(0);
  const [openSettingsMenu, setOpenSettingsMenu] = useState<boolean>(false);

  const handleOpenSettingsMenu = (openSettingMenu: boolean) =>
    setOpenSettingsMenu(openSettingMenu);

  const handleActiveTab = (activeTab: number) => setActiveTab(activeTab);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const toggleSettings = (e: React.MouseEvent<HTMLElement>) => {
    if (!openSettingsMenu) {
      setAnchorEl(e.currentTarget);
    }
    globalLayersControlRef.current?.openLayerControl(false);
    setOpenSettingsMenu(!openSettingsMenu);
  };

  const applyFiltersClick = () => {
    applySettingsClicked();
    closeSettings();
  };

  useImperativeHandle(ref, () => ({
    activeTab,
    toggleSettings,
    handleActiveTab,
    openSettingsMenu,
    handleOpenSettingsMenu,
  }));

  return (
    <>
      {openSettingsMenu && (
        <Menu
          anchorEl={anchorEl}
          open={openSettingsMenu}
          onClose={closeSettings}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          className="filters-menu-container"
        >
          <Box className={'filters-menu large-map-filters'}>
            <Grid container>
              <Grid item xs={2}>
                <Tabs
                  value={activeTab}
                  onChange={handleChange}
                  aria-label="simple tabs example"
                  orientation="vertical"
                  variant="scrollable"
                  className="tabs-navigation"
                >
                  <Tab label="Technology KPIs" {...a11yProps(0)} />
                  <Tab label="SQM KQIs" {...a11yProps(1)} />
                  <Tab label="Neighbors kpis" {...a11yProps(2)} />
                  <Tab label="Cell Filters" {...a11yProps(3)} />
                  <Tab label="Marker Labels" {...a11yProps(4)} />
                </Tabs>
              </Grid>
              <Grid item xs={10}>
                <TabPanel value={activeTab} index={0}>
                  <Box className={'filters-container'}>
                    <Grid container columnSpacing={3}>
                      {initialFilters
                        .filter((filter) => filter.category === 'mapKpiFilter')
                        .map((filter, i) => (
                          <Grid
                            item
                            key={i}
                            xs={
                              12 /
                              initialFilters.filter(
                                (filter) => filter.category === 'mapKpiFilter'
                              ).length
                            }
                          >
                            <FilterComponent
                              filter={filter}
                              dashboardId={dashboardId}
                            />
                          </Grid>
                        ))}
                    </Grid>
                  </Box>
                </TabPanel>
                <TabPanel value={activeTab} index={1}>
                  <Box className={'filters-container'}>
                    <Grid container columnSpacing={3}>
                      {initialFilters
                        .filter((filter) => filter.category === 'mapKqiFilter')
                        .map((filter, i) => (
                          <Grid
                            item
                            key={i}
                            xs={
                              12 /
                              initialFilters.filter(
                                (filter) => filter.category === 'mapKqiFilter'
                              ).length
                            }
                          >
                            <FilterComponent
                              filter={filter}
                              dashboardId={dashboardId}
                            />
                          </Grid>
                        ))}
                    </Grid>
                  </Box>
                </TabPanel>
                <TabPanel value={activeTab} index={2}>
                  <Box className={'filters-container'}>
                    <Grid container columnSpacing={3}>
                      {initialFilters
                        .filter(
                          (filter) => filter.category === 'mapNeighborKpiFilter'
                        )
                        .map((filter, i) => (
                          <Grid
                            item
                            key={i}
                            xs={
                              12 /
                              initialFilters.filter(
                                (filter) =>
                                  filter.category === 'mapNeighborKpiFilter'
                              ).length
                            }
                          >
                            <FilterComponent
                              filter={filter}
                              dashboardId={dashboardId}
                            />
                          </Grid>
                        ))}
                    </Grid>
                  </Box>
                </TabPanel>
                <TabPanel value={activeTab} index={3}>
                  <Box className={'filters-container'}>
                    <Grid container columnSpacing={3}>
                      {initialFilters
                        .filter((filter) => filter.category === 'localFilter')
                        .map((filter, i) => (
                          <Grid
                            item
                            key={i}
                            xs={
                              12 /
                              initialFilters.filter(
                                (filter) => filter.category === 'localFilter'
                              ).length
                            }
                          >
                            <FilterComponent
                              filter={filter}
                              dashboardId={dashboardId}
                            />
                          </Grid>
                        ))}
                    </Grid>
                  </Box>
                </TabPanel>
                <TabPanel value={activeTab} index={4}>
                  <Box className={'filters-container'}>
                    <Grid container columnSpacing={3}>
                      {initialFilters
                        .filter(
                          (filter) => filter.category === 'cellIdentifier'
                        )
                        .map((filter, i) => (
                          <Grid
                            item
                            key={i}
                            xs={
                              12 /
                              initialFilters.filter(
                                (filter) => filter.category === 'cellIdentifier'
                              ).length
                            }
                          >
                            <FilterComponent
                              filter={filter}
                              dashboardId={dashboardId}
                            />
                          </Grid>
                        ))}
                    </Grid>
                  </Box>
                </TabPanel>
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{ px: 2 }}
                  display={'flex'}
                  justifyContent={'flex-end'}
                >
                  <Button
                    onClick={() => closeSettings()}
                    className="secondary-btn"
                    color="secondary"
                    variant="contained"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => applyFiltersClick()}
                    className="primary-btn"
                    color="primary"
                    variant="contained"
                  >
                    Apply
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Menu>
      )}
    </>
  );
};

export default forwardRef<SettingsFiltersRef, SettingsFiltersProps>(
  SettingsFilters
);
